/**
 * Deterministic GSTR-2B Reconciliation & IMS (Invoice Management System) Engine
 * Replicated & adapted from resilient-tech/india-compliance (gstr_2 utils & ims fields)
 * apps/gst-portal/src/services/reconciliationService.ts
 */

import Decimal from 'decimal.js';
import {
  ExtractedGstInvoice,
  Gstr2bRecord,
  ReconStatus,
  ReconciliationSummary,
  ImsAction
} from '../types/gst';

/**
 * Computes Levenshtein edit distance between two strings
 */
export function computeLevenshteinDistance(a: string, b: string): number {
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;

  const matrix: number[][] = [];
  for (let i = 0; i <= bl; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= al; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= bl; i++) {
    for (let j = 1; j <= al; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[bl][al];
}

/**
 * Normalizes invoice number string per India Compliance standards:
 * Strips special characters, removes leading zeroes, uppercase
 */
export function normalizeInvoiceNumber(raw: string): string {
  if (!raw) return '';
  return raw
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/^0+/, '');
}

/**
 * Detects if line item or invoice is blocked under Section 17(5) of CGST Act:
 * Food & beverages (HSN 9963), passenger motor vehicles (HSN 8702/8703), club/gym (HSN 9997), personal gifts
 */
export function detectSection17_5BlockedCredit(invoice: ExtractedGstInvoice): {
  isBlocked: boolean;
  reason?: string;
} {
  const blockedHsnPrefixes = ['9963', '8702', '8703', '9997'];
  const blockedKeywords = [
    'food', 'catering', 'restaurant', 'meal', 'beverage', 'alcohol',
    'motor vehicle', 'car rental', 'cab service', 'club membership',
    'fitness', 'gym', 'health insurance', 'life insurance', 'personal gift'
  ];

  for (const item of invoice.line_items || []) {
    const hsn = (item.hsn_sac_code || '').trim();
    for (const prefix of blockedHsnPrefixes) {
      if (hsn.startsWith(prefix)) {
        return {
          isBlocked: true,
          reason: `Blocked under Section 17(5) (SAC ${hsn}): Ineligible ITC on food, catering, club or passenger vehicles.`
        };
      }
    }

    const desc = (item.description || '').toLowerCase();
    for (const kw of blockedKeywords) {
      if (desc.includes(kw)) {
        return {
          isBlocked: true,
          reason: `Blocked under Section 17(5): Item description "${item.description}" indicates personal/ineligible supply.`
        };
      }
    }
  }

  return { isBlocked: false };
}

export interface ReconMatchResult {
  invoice: ExtractedGstInvoice;
  matched2bRecord?: Gstr2bRecord;
  reconStatus: ReconStatus;
  matchType: 'EXACT' | 'FUZZY_INVOICE' | 'DATE_MATCH_TAX_MISMATCH' | 'TAX_MISMATCH' | 'MISSING';
  taxVariance: number;
  explanation: string;
  recommendedImsAction: ImsAction;
  selectedImsAction?: ImsAction;
  isBlockedSec17_5: boolean;
}

/**
 * Deterministic 3-Way GSTR-2B Reconciliation Engine with India Compliance IMS Integration
 */
export function runGstr2bReconciliation(
  purchaseInvoices: ExtractedGstInvoice[],
  gstr2bRecords: Gstr2bRecord[],
  savedImsDecisions: Record<string, ImsAction> = {}
): {
  results: ReconMatchResult[];
  summary: ReconciliationSummary;
} {
  const matched2bRecordIds = new Set<string>();
  const results: ReconMatchResult[] = [];

  // Filter for B2B Purchases only
  const b2bInvoices = purchaseInvoices.filter(
    inv => inv.category === 'B2B_PURCHASES' || inv.category === 'EXPENSE_RECEIPT'
  );

  for (const inv of b2bInvoices) {
    const invGstin = (inv.supplier_gstin || "").trim().toUpperCase();
    const invNormNum = normalizeInvoiceNumber(inv.normalized_invoice_number || inv.invoice_number || "");
    const invTax = new Decimal(inv.total_tax || 0);

    // Check Sec 17(5)
    const sec17_5Check = detectSection17_5BlockedCredit(inv);
    const isBlocked = inv.itc_eligibility === 'BLOCKED_17_5' || sec17_5Check.isBlocked;

    // Candidates with matching supplier GSTIN that haven't been matched yet
    const vendor2bRecords = gstr2bRecords.filter(
      r => (r.supplier_gstin || "").trim().toUpperCase() === invGstin && !matched2bRecordIds.has(r.id)
    );

    let bestMatch: Gstr2bRecord | null = null;
    let reconStatus: ReconStatus = 'MISSING_IN_2B';
    let matchType: ReconMatchResult['matchType'] = 'MISSING';
    let taxVariance = 0;
    let explanation = "Not found in GSTR-2B portal data for this tax period.";
    let recommendedAction: ImsAction = 'PENDING';

    // PASS 1: Exact Match (supplier_gstin + normalized_invoice_number match and total_tax difference <= ₹1.00)
    for (const record of vendor2bRecords) {
      const recNormNum = normalizeInvoiceNumber(record.normalized_invoice_number || record.invoice_number || "");
      const recTotalTax = new Decimal(record.integrated_tax || 0)
        .plus(record.central_tax || 0)
        .plus(record.state_tax || 0)
        .plus(record.cess_amount || 0);

      const variance = invTax.minus(recTotalTax).abs().toNumber();

      if (invNormNum && recNormNum && invNormNum === recNormNum) {
        if (variance <= 1.00) {
          bestMatch = record;
          reconStatus = 'EXACT_MATCH';
          matchType = 'EXACT';
          taxVariance = Number(variance.toFixed(2));
          explanation = `Exact match in GSTR-2B. Tax variance ₹${taxVariance.toFixed(2)} is within ₹1.00 tolerance.`;
          recommendedAction = isBlocked ? 'REJECT' : 'ACCEPT';
          break;
        } else if (variance <= 5.00) {
          bestMatch = record;
          reconStatus = 'EXACT_MATCH';
          matchType = 'EXACT';
          taxVariance = Number(variance.toFixed(2));
          explanation = `Exact match with minor rounding variance ₹${taxVariance.toFixed(2)} (within ₹5.00 limit).`;
          recommendedAction = isBlocked ? 'REJECT' : 'ACCEPT';
          break;
        } else {
          // Same invoice number but tax mismatch > ₹5.00
          bestMatch = record;
          reconStatus = 'PARTIAL_MATCH';
          matchType = 'TAX_MISMATCH';
          taxVariance = Number(variance.toFixed(2));
          explanation = `Invoice number matches, but tax mismatch of ₹${taxVariance.toFixed(2)} (Books: ₹${invTax.toFixed(2)}, Portal: ₹${recTotalTax.toFixed(2)}).`;
          recommendedAction = 'PENDING';
          break;
        }
      }
    }

    // PASS 2: Partial Match if no exact invoice number match was found
    if (!bestMatch) {
      for (const record of vendor2bRecords) {
        const recNormNum = normalizeInvoiceNumber(record.normalized_invoice_number || record.invoice_number || "");
        const recTotalTax = new Decimal(record.integrated_tax || 0)
          .plus(record.central_tax || 0)
          .plus(record.state_tax || 0)
          .plus(record.cess_amount || 0);

        const variance = invTax.minus(recTotalTax).abs().toNumber();

        // Check fuzzy invoice number (Levenshtein distance <= 2)
        const distance = (invNormNum && recNormNum) ? computeLevenshteinDistance(invNormNum, recNormNum) : 999;
        const dateMatch = Boolean(inv.invoice_date && record.invoice_date && inv.invoice_date === record.invoice_date);

        if (distance <= 2 || dateMatch) {
          bestMatch = record;
          reconStatus = 'PARTIAL_MATCH';
          matchType = distance <= 2 ? 'FUZZY_INVOICE' : 'DATE_MATCH_TAX_MISMATCH';
          taxVariance = Number(variance.toFixed(2));
          explanation = distance <= 2
            ? `Fuzzy invoice number match (Books: ${inv.invoice_number}, Portal: ${record.invoice_number}, edit distance: ${distance}). Tax variance ₹${taxVariance.toFixed(2)}.`
            : `Invoice date matches (${inv.invoice_date}), but invoice numbers differ (Books: ${inv.invoice_number}, Portal: ${record.invoice_number}).`;
          recommendedAction = 'PENDING';
          break;
        }
      }
    }

    if (bestMatch) {
      matched2bRecordIds.add(bestMatch.id);
    }

    const savedAction = savedImsDecisions[inv.id];

    results.push({
      invoice: {
        ...inv,
        recon_status: reconStatus,
        itc_eligibility: isBlocked ? 'BLOCKED_17_5' : inv.itc_eligibility
      },
      matched2bRecord: bestMatch || undefined,
      reconStatus,
      matchType,
      taxVariance,
      explanation: isBlocked && sec17_5Check.reason ? `${explanation} • ${sec17_5Check.reason}` : explanation,
      recommendedImsAction: recommendedAction,
      selectedImsAction: savedAction || recommendedAction,
      isBlockedSec17_5: isBlocked
    });
  }

  // Calculate summary metrics
  let totalPurchaseItc = new Decimal(0);
  let matchedItc = new Decimal(0);
  let partialItc = new Decimal(0);
  let missingItc = new Decimal(0);
  let blockedItc = new Decimal(0);
  let rcmItc = new Decimal(0);

  let matchedCount = 0;
  let partialCount = 0;
  let missingCount = 0;
  let blockedCount = 0;
  let rcmCount = 0;

  for (const item of results) {
    const tax = new Decimal(item.invoice.total_tax || 0);
    totalPurchaseItc = totalPurchaseItc.plus(tax);

    if (item.isBlockedSec17_5) {
      blockedCount++;
      blockedItc = blockedItc.plus(tax);
    } else if (item.invoice.itc_eligibility === 'RCM_APPLICABLE') {
      rcmCount++;
      rcmItc = rcmItc.plus(tax);
    }

    if (item.reconStatus === 'EXACT_MATCH') {
      matchedCount++;
      matchedItc = matchedItc.plus(tax);
    } else if (item.reconStatus === 'PARTIAL_MATCH') {
      partialCount++;
      partialItc = partialItc.plus(tax);
    } else if (item.reconStatus === 'MISSING_IN_2B') {
      missingCount++;
      missingItc = missingItc.plus(tax);
    }
  }

  const summary: ReconciliationSummary = {
    totalPurchaseInvoices: b2bInvoices.length,
    totalPurchaseItc: totalPurchaseItc.toDecimalPlaces(2).toNumber(),
    matchedCount,
    matchedItc: matchedItc.toDecimalPlaces(2).toNumber(),
    partialCount,
    partialItc: partialItc.toDecimalPlaces(2).toNumber(),
    missingIn2bCount: missingCount,
    missingIn2bItc: missingItc.toDecimalPlaces(2).toNumber(),
    blockedCount,
    blockedItc: blockedItc.toDecimalPlaces(2).toNumber(),
    rcmCount,
    rcmItc: rcmItc.toDecimalPlaces(2).toNumber()
  };

  return { results, summary };
}

/**
 * Generates automated 1-click WhatsApp and Email nudge copy for vendors with missing invoices in GSTR-2B
 */
export function generateVendorNudge(
  invoice: ExtractedGstInvoice,
  clientCompanyName: string = "Our Company",
  buyerGstin: string = ""
): {
  subject: string;
  body: string;
  whatsappUrl: string;
  mailToUrl: string;
} {
  const invNum = invoice.invoice_number || "N/A";
  const invDate = invoice.invoice_date || "N/A";
  const tax = Number(invoice.total_tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const total = Number(invoice.invoice_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const vendorName = invoice.supplier_name || "Vendor Partner";
  const period = `${invoice.taxPeriod} (${invoice.financialYear})`;

  const subject = `Urgent: GST ITC Reflection for Inv #${invNum} [${clientCompanyName}]`;

  const body = `Dear ${vendorName},

Greetings from ${clientCompanyName}.

We are currently filing our GST returns for ${period}. During our automated GSTR-2B reconciliation, we noticed that the following tax invoice has not yet appeared in our GSTR-2B on the GST portal:

• Invoice Number: ${invNum}
• Invoice Date: ${invDate}
• Invoice Total: ₹${total}
• Tax Amount (ITC): ₹${tax}
• Recipient GSTIN: ${buyerGstin || invoice.buyer_gstin || "our registered GSTIN"}

As per Section 16(2)(aa) of the CGST Act, Input Tax Credit cannot be claimed unless the invoice is reflected in GSTR-2B. 

Could you please verify that this invoice was correctly uploaded in your GSTR-1 and submitted? If already filed, please confirm the ARN or filing period.

Thank you for your prompt assistance!

Warm regards,
Accounts & Finance Team
${clientCompanyName}`;

  const encodedBody = encodeURIComponent(body);
  const encodedSubject = encodeURIComponent(subject);

  const whatsappMessage = `*GST ITC Follow-up from ${clientCompanyName}*
Hi ${vendorName}, we noticed Invoice #${invNum} (dated ${invDate}, Tax: ₹${tax}) is missing in our GSTR-2B portal records for ${period}.
Could you please ensure this is filed in your GSTR-1 against our GSTIN (${buyerGstin || invoice.buyer_gstin}) so our ITC is not blocked?
Thank you!`;

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
  const mailToUrl = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;

  return { subject, body, whatsappUrl, mailToUrl };
}
